import { useState, useRef, useEffect } from 'react';

export interface EditableTextareaProps {
  /** Current value to display */
  value: string | undefined | null;
  /** Called when value is saved - can be async. Value is string or null if cleared */
  onSave: (value: string | null) => void | Promise<void>;
  /** Whether editing is disabled */
  disabled?: boolean;
  /** Placeholder text when no value is set */
  placeholder?: string;
  /** Minimum number of rows for the textarea */
  minRows?: number;
  /** Custom class for additional styling */
  className?: string;
}

export function EditableTextarea({
  value,
  onSave,
  disabled = false,
  placeholder = 'Click to add notes...',
  minRows = 3,
  className = '',
}: EditableTextareaProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      // Move cursor to end
      textareaRef.current.selectionStart = textareaRef.current.value.length;
    }
  }, [isEditing]);

  const startEditing = () => {
    if (disabled || isSaving) return;
    setInputValue(value || '');
    setIsEditing(true);
  };

  const save = async () => {
    const trimmedValue = inputValue.trim();
    const newValue = trimmedValue === '' ? null : trimmedValue;
    const oldValue = value || null;

    if (newValue !== oldValue) {
      setIsSaving(true);
      try {
        await onSave(newValue);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      } catch {
        setShowError(true);
        setTimeout(() => setShowError(false), 2000);
      } finally {
        setIsSaving(false);
      }
    }
    setIsEditing(false);
  };

  const cancel = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Escape cancels, Cmd/Ctrl+Enter saves
    if (e.key === 'Escape') {
      cancel();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      save();
    }
  };

  const baseStyles = 'w-full py-2 px-3 text-sm rounded-md border transition-all duration-200';

  // Success/error state: solid border
  const statusStyles = showSuccess ? 'border-primary-500' : showError ? 'border-red-500' : '';

  // Saving state: pulsing glow
  const savingStyles = isSaving
    ? 'border-primary-500 shadow-[0_0_0_3px_rgba(44,160,28,0.4)] animate-pulse'
    : '';

  if (isEditing) {
    return (
      <div className="space-y-2">
        <textarea
          ref={textareaRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={save}
          onKeyDown={handleKeyDown}
          rows={minRows}
          className={`${baseStyles} border-gray-300 bg-white text-gray-700 outline-none focus:border-gray-400 resize-y ${savingStyles} ${statusStyles} ${className}`}
        />
        <p className="text-xs text-gray-400">Press Cmd+Enter to save, Escape to cancel</p>
      </div>
    );
  }

  const hasValue = value && value.trim() !== '';
  const hasStatus = showSuccess || showError || isSaving;
  const borderStyle = hasStatus ? '' : 'border-transparent hover:border-gray-300';

  return (
    <button
      type="button"
      onClick={startEditing}
      disabled={disabled || isSaving}
      className={`${baseStyles} ${borderStyle} text-left focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed ${savingStyles} ${statusStyles} ${className}`}
    >
      {hasValue ? (
        <span className="text-gray-600 whitespace-pre-wrap">{value}</span>
      ) : (
        <span className="text-gray-400 italic">{placeholder}</span>
      )}
    </button>
  );
}
